import { useState, useEffect } from "react";
import "./Decrypt.css";
import { toast } from "react-toastify";
import { useNavigate } from "../../hooks/useRouter";
import CryptoJS from 'crypto-js';
import { encrypted } from "../../utils/utility";

// utility & context
import { useConstants } from "../../constant/constantProvider";
import { useAuth } from "../../hooks/useAuth";

// assets
import DECRYPT_ICON from "../../assets/images/translation.svg";
import copyInactive from "../../assets/new-images/copy-icon.svg";
import copyActive from "../../assets/new-images/active-copy-icon.svg";

const Decrypt = () => {
  const { DECRYPT_AI, ENCRYPTED_TEXT_PLACEHOLDER, DECRYPTED_TEXT_PLACEHOLDER, DECRYPT_BUTTON, ENCRYPTED_TEXT_ERROR, DECRYPT_SUCCESS, COPY_SUCCESS } = useConstants();
  const { ugdnId: userUGDN } = useAuth();
  const navigate = useNavigate();

  // Hashed UGDN IDs to avoid exposing sensitive information (emails/usernames) in source code
  const ALLOWED_DECRYPT_HASHES = [
    "ea6489e7040cc58a8e21c25f0ef6f353a2685872addbbf9eaba84e1a0c4d4ad7",
    "b23cdab7fd6c829fea225c409b52c6e321e977deab3c008c3a16ac1f5ae233ff",
    "40fb5d82e59def410f2378a09486c19d024e68ea1bc2311b83a94c148948fcea",
    "1eeef59179607e0a991cc5dfae022d49bb53e5f87043825428cae52b4a9705eb",
    "c6e15fa2493fe10dc8f8dd859bdd96dc7686772eced0d6099dbaa13489f0bc76",
    "82f694fd5e62574fed9a85c2d02833edfdf543445dc5929ec22240dfa02d2083",
    "6ec24468adaab85b645f6814f6d19250e47d810694a8ca82a5ca4083662db9ec"
  ];

  // Hash the current user's ID to safely check authorization
  const userHash = userUGDN ? CryptoJS.SHA256(userUGDN).toString() : "";
  const canDecrypt = ALLOWED_DECRYPT_HASHES.includes(userHash);

  // Redirect unauthorized users to /chat
  useEffect(() => {
    if (userUGDN && !canDecrypt) {
      navigate("/chat");
    }
  }, [userUGDN, canDecrypt, navigate]);

  const [encryptedText, setEncryptedText] = useState("");
  const [decryptionKey, setDecryptionKey] = useState("");
  const [decryptedText, setDecryptedText] = useState("");
  const [formattedDecryptedText, setFormattedDecryptedText] = useState("");
  const [isJsonFormat, setIsJsonFormat] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Helper function to detect and format JSON
  const formatJsonIfValid = (text) => {
    try {
      const parsed = JSON.parse(text);
      setIsJsonFormat(true);
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      setIsJsonFormat(false);
      return text;
    }
  };

  // Helper function to decrypt with a custom key
  const decryptWithKey = async (encryptedText, key) => {
    let decrypted = null;

    // First attempt: CBC mode
    try {
      const parsedBase64Key = CryptoJS.enc.Base64.parse(key);
      const decodedText = CryptoJS.enc.Base64.parse(encryptedText);
      const iv = CryptoJS.lib.WordArray.create(decodedText.words.slice(0, 4));
      const cipherText = CryptoJS.lib.WordArray.create(decodedText.words.slice(4));
      const decryptedData = CryptoJS.AES.decrypt(
        { ciphertext: cipherText },
        parsedBase64Key,
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      );
      const result = decryptedData.toString(CryptoJS.enc.Utf8);
      if (result) {
        decrypted = result;
      }
    } catch (error) {
      // Ignore CBC errors and try GCM next
    }

    // Second attempt: GCM mode
    if (!decrypted) {
      try {
        const rawKey = Uint8Array.from(atob(key), (c) => c.charCodeAt(0));
        const cryptoKey = await crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['decrypt']);

        const combined = Uint8Array.from(atob(encryptedText), (c) => c.charCodeAt(0));
        const nonce = combined.slice(0, 12);
        const cipherText = combined.slice(12);

        const decryptedBuffer = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: nonce },
          cryptoKey,
          cipherText
        );
        decrypted = new TextDecoder().decode(decryptedBuffer);
      } catch (error) {
        // Ignore GCM errors
      }
    }

    if (!decrypted) {
      throw new Error('Decryption failed with custom key');
    }
    return decrypted;
  };

  const handleDecryptClick = async () => {
    if (!encryptedText.trim()) {
      toast.error(ENCRYPTED_TEXT_ERROR);
      return;
    }

    setIsDecrypting(true);
    try {
      // Clean the input - remove surrounding quotes if present
      let cleanText = encryptedText.trim();
      if (cleanText.startsWith('"') && cleanText.endsWith('"')) {
        cleanText = cleanText.slice(1, -1);
      } else if (cleanText.startsWith("'") && cleanText.endsWith("'")) {
        cleanText = cleanText.slice(1, -1);
      }

      let decrypted;
      if (decryptionKey.trim()) {
        // Use custom key for decryption
        decrypted = await decryptWithKey(cleanText, decryptionKey.trim());
      } else {
        // Use default key for decryption
        try {
          decrypted = encrypted.decrypt(cleanText);
        } catch (error) {
          decrypted = null;
        }
      }

      if (decrypted && decrypted.trim()) {
        const formattedText = formatJsonIfValid(decrypted);
        setDecryptedText(decrypted);
        setFormattedDecryptedText(formattedText);
        toast.info(DECRYPT_SUCCESS);
      } else {
        toast.error("Failed to decrypt. The encrypted text may be invalid or the decryption key is incorrect.");
        setDecryptedText("");
        setFormattedDecryptedText("");
      }
    } catch (error) {
      console.error("Decryption error:", error);
      toast.error("Failed to decrypt. Please check your encrypted text format and key.");
      setDecryptedText("");
      setFormattedDecryptedText("");
    } finally {
      setIsDecrypting(false);
    }
  };

  const handleCopyClick = async () => {
    if (!decryptedText) return;

    try {
      // Copy as JSON if it's JSON format, otherwise copy original text
      const textToCopy = isJsonFormat ? formattedDecryptedText : decryptedText;
      const clipboardItem = new ClipboardItem({
        "text/plain": new Blob([textToCopy], { type: "text/plain" }),
        "text/html": new Blob([`<div>${textToCopy}</div>`], { type: "text/html" }),
      });

      await navigator.clipboard.write([clipboardItem]);

      setCopied(true);
      toast.info(COPY_SUCCESS, { autoClose: 1000 });
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error("Failed to copy.");
    }
  };

  const resetAll = () => {
    setEncryptedText("");
    setDecryptionKey("");
    setDecryptedText("");
    setFormattedDecryptedText("");
    setIsJsonFormat(false);
    setCopied(false);
  };

  const clearText = () => {
    setEncryptedText("");
    setDecryptedText("");
    setFormattedDecryptedText("");
    setIsJsonFormat(false);
    setCopied(false);
  };

  if (userUGDN && !canDecrypt) {
    return null;
  }

  return (
    <div className="decrypt-container">
      <div className="decrypt-inner-container">
        <div className="decrypt-heading-section">
          <img src={DECRYPT_ICON} alt="Decrypt" />
          <p className="decrypt-text">{DECRYPT_AI}</p>
        </div>

        <div className="decrypt-key-section">
          <input
            type="text"
            className="decrypt-input"
            placeholder="Enter decryption key (optional - uses default key if empty)"
            value={decryptionKey}
            onChange={(e) => setDecryptionKey(e.target.value)}
          />
        </div>

        <div className="decrypt-zone-main-section">
          <div className="decrypt-text-section">
            <div className="block-part">
              <div className="textarea-wrapper">
                <textarea
                  className="decrypt-textarea"
                  placeholder={ENCRYPTED_TEXT_PLACEHOLDER}
                  value={encryptedText}
                  onChange={(e) => setEncryptedText(e.target.value)}
                />
              </div>
            </div>
            <div className="line"></div>
            <div className="block-part">
              <div className="textarea-wrapper">
                <textarea
                  className={`decrypt-textarea ${isJsonFormat ? 'json-format' : ''}`}
                  placeholder={DECRYPTED_TEXT_PLACEHOLDER}
                  value={formattedDecryptedText}
                  readOnly
                />
                {formattedDecryptedText && !isDecrypting && (
                  <div className="decrypt-feedback-section">
                    {isJsonFormat && (
                      <span className="json-indicator">JSON</span>
                    )}
                    <button
                      className="button-style"
                      disabled={copied}
                      onClick={handleCopyClick}
                    >
                      <img
                        src={copied ? copyActive : copyInactive}
                        className="feedback-image"
                        alt="copy icon"
                      />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="decrypt-button-section">
          <button
            className={`secondary-button button-style ${!encryptedText.trim() ? "disabled-button" : ""}`}
            type="button"
            disabled={!encryptedText.trim() || isDecrypting}
            onClick={handleDecryptClick}
          >
            {isDecrypting ? "Decrypting..." : DECRYPT_BUTTON}
          </button>
          <button
            className="decrypt-outline-button button-style"
            type="button"
            onClick={clearText}
          >
            Clear Text
          </button>
          <button
            className="decrypt-outline-button button-style"
            type="button"
            onClick={resetAll}
          >
            Reset All
          </button>
        </div>
      </div>
    </div>
  );
};

export default Decrypt;
